import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, ArrowRight, Youtube } from 'lucide-react';
import { socialMedia } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const location = useLocation();
  const { t } = useLanguage();
  
  // Hide footer on homepage
  if (location.pathname === '/') {
    return null;
  }

  const quickLinks = [
    { path: '/biography', label: t('biography') },
    { path: '/horses', label: t('horses') },
    { path: '/calendar', label: t('calendar') },
    { path: '/news', label: t('news') },
    { path: '/photos', label: t('photos') }
  ];

  const moreLinks = [
    { path: '/blog', label: t('blog') },
    { path: '/shop', label: t('shop') },
    { path: '/sponsors', label: t('sponsors') }
  ];

  return (
    <footer data-testid="footer" className="bg-black border-t border-zinc-800 text-zinc-400">
      <div className="container mx-auto px-8 md:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="/logo-krew.png" 
                alt="KREW Logo" 
                className="h-16 w-auto"
              />
            </Link>
            <h3 className="text-white text-xl font-black tracking-wider mb-2">GIACOMO BASSI</h3>
            <p className="text-sm leading-relaxed">
              {t('footerDesc')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white text-sm font-bold mb-6 uppercase tracking-widest">{t('navigate')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm hover:text-pink-500 transition-colors inline-flex items-center gap-2 group"
                  >
                    <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-pink-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* More Links */}
          <div>
            <h4 className="text-white text-sm font-bold mb-6 uppercase tracking-widest">{t('more')}</h4>
            <ul className="space-y-3">
              {moreLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm hover:text-cyan-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-cyan-400" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-bold mb-6 uppercase tracking-widest">{t('connect')}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-pink-500 flex-shrink-0" />
                <span className="text-sm">{t('italy')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <a 
                  href="mailto:g.bassirider@icloud.com" 
                  className="text-sm hover:text-white transition-colors"
                >
                  g.bassirider@icloud.com
                </a>
              </li>
              <li>
                <div className="flex gap-3 mt-4">
                  <a
                    href={socialMedia.instagram.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-700 flex items-center justify-center hover:border-pink-500 hover:text-pink-500 transition-colors"
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href={socialMedia.tiktok.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-700 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 transition-colors"
                  >
                    <TikTokIcon className="w-[18px] h-[18px]" />
                  </a>
                  <a
                    href={socialMedia.facebook.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-700 flex items-center justify-center hover:border-blue-500 hover:text-blue-500 transition-colors"
                  >
                    <Facebook size={18} />
                  </a>
                  <a
                    href={socialMedia.youtube.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-zinc-700 flex items-center justify-center hover:border-red-500 hover:text-red-500 transition-colors"
                  >
                    <Youtube size={18} />
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-zinc-500">
            &copy; {new Date().getFullYear()} Giacomo Bassi. {t('allRightsReserved')}
          </p>
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <span>{t('poweredBy')}</span>
            <span className="font-bold bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">KREW</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
